/**
 * Forgot Password Page
 *
 * Allows users to request a password reset link by email.
 */

import React, { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ErrorMessage, Input, SuccessMessage } from '../components/AuthForm';

type RequestStatus = 'idle' | 'loading' | 'success' | 'error';

export function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<RequestStatus>('idle');
  const [message, setMessage] = useState('');

  const isSubmitting = status === 'loading';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage('');

    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    try {
      setStatus('loading');
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => null);

      // Always show success to prevent email enumeration
      setStatus('success');
      setMessage(
        data?.data?.message ||
          'If an account exists with this email, you will receive a password reset link.'
      );
      setEmail('');
    } catch (error) {
      // Still show success message to prevent email enumeration
      setStatus('success');
      setMessage('If an account exists with this email, you will receive a password reset link.');
      setEmail('');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-color, #f9fafb)',
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          backgroundColor: 'var(--card-bg, #ffffff)',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 700,
              color: 'var(--text-color, #1f2937)',
              marginBottom: '8px',
            }}
          >
            Forgot your password?
          </h1>
          <p style={{ color: 'var(--text-muted, #6b7280)', fontSize: '14px' }}>
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {message && status === 'error' && (
          <div style={{ marginBottom: '16px' }}>
            <ErrorMessage message={message} />
          </div>
        )}

        {message && status === 'success' && (
          <div style={{ marginBottom: '16px' }}>
            <SuccessMessage message={message} />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Input
              type="email"
              name="email"
              placeholder="Email address"
              value={email}
              onChange={setEmail}
              required
              autoComplete="email"
            />
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Send reset link
            </Button>
          </div>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Button variant="secondary" onClick={() => navigate('/signin')} disabled={isSubmitting}>
            Back to sign in
          </Button>
        </div>
      </div>
    </div>
  );
}
